/**
 * SearchSpring Beacon v2 Tracking
 *
 * Fires client-side beacon events to SS for reporting and personalization:
 * - {module}/render: when results are rendered on the page (every response,
 *   including zero-results — a render with no impressions is what feeds the
 *   Zero Results report)
 * - {module}/impression: when a product card enters the viewport (50% visible)
 * - {module}/clickthrough: when a user clicks a product card
 *
 * The client-side render event is REQUIRED for every module (HAN-403/404/405):
 * it anchors the Beacon 2.0 event chain that the Searches 2.0, Categories 2.0,
 * Filters, and Autocomplete reports are built on. `beacon=true` on the API only
 * generates the server-side {module}.results event — it does NOT replace render.
 * (Same class of bug as HAN-394, where the missing recommendations render left
 * Impression Rate Null.)
 *
 * Payload shapes per module (per docs.searchspring.com tracking guides):
 * - search/category render: data = { responseId } only
 * - recommendations render: data = { tag, responseId, results } (profile tag
 *   required or SS drops the event)
 * {module}/addtocart is not implemented — SS cards don't have quick-add yet.
 *
 * Beacon v2 endpoint: https://analytics.searchspring.net/beacon/v2/{siteId}/{module}/{event}
 *
 * Config options:
 * - siteId: SS site ID (required)
 * - pageLoadId: unique page load identifier (required)
 * - module: beacon module path — 'category' for collections, 'search' for search results (default: 'category')
 * - gridSelector: CSS selector for the results grid element (default: '#ss-results-grid')
 * - tag: recommendations profile tag — REQUIRED by SS for `recommendations` events,
 *   ignored for category/search. Added to the event `data` so impression/clickthrough
 *   are attributed to the profile (otherwise SS drops them and CTR stays blank).
 *
 * Loaded as a defer IIFE — initialized by page-specific orchestrators.
 * Grid-based modules (category/search/recommendations) call init(), which wires
 * the ss:results-rendered / ss:cards-appended listeners and grid click handler.
 * Modules that render outside the standard grid flow (autocomplete) skip init()
 * and drive tracking directly via trackResults() / trackClickthrough().
 */

(function () {
  'use strict';

  const USER_ID_KEY = 'ss-userId';
  const SESSION_ID_KEY = 'ss-sessionId';
  const IMPRESSION_THRESHOLD = 0.5;
  const BEACON_BASE = 'https://analytics.searchspring.net/beacon/v2';

  // SS rejects events whose context.userId/sessionId are empty ("missing
  // required key-value" in the Beacon Data Quality report). When storage is
  // unavailable (Safari private browsing, blocked cookies) fall back to
  // in-memory IDs so every event this page load still validates.
  const memoryIds = {};

  function getOrCreateContextId(key, storageType) {
    try {
      const storage = storageType === 'local' ? localStorage : sessionStorage;
      let id = storage.getItem(key);
      if (!id) {
        id = crypto.randomUUID();
        storage.setItem(key, id);
      }
      return id;
    } catch {
      if (!memoryIds[key]) {
        memoryIds[key] = crypto.randomUUID();
      }
      return memoryIds[key];
    }
  }

  class SSBeacon {
    #siteId;
    #pageLoadId;
    #module;
    #gridSelector;
    #tag;
    #observer;
    #impressedProducts = new Set();
    #lastResponseId;

    constructor(config) {
      this.#siteId = config.siteId;
      this.#pageLoadId = config.pageLoadId;
      this.#module = config.module || 'category';
      this.#gridSelector = config.gridSelector || '#ss-results-grid';
      this.#tag = config.tag || '';
      this.#observer = null;
    }

    init() {
      document.addEventListener('ss:results-rendered', (e) => {
        if (!this.#isOwnGridEvent(e)) return;
        this.#onResultsRendered(e.detail?.response);
      });

      // Infinite scroll appends cards without a full re-render —
      // observe only the new cards without resetting the existing observer
      document.addEventListener('ss:cards-appended', (e) => {
        if (!this.#isOwnGridEvent(e)) return;
        this.#onCardsAppended(e.detail);
      });

      const grid = document.querySelector(this.#gridSelector);
      if (grid) {
        grid.addEventListener('click', (e) => this.#onProductClick(e));
      }
    }

    /**
     * ss:results-rendered / ss:cards-appended are global events shared by every
     * SS module on the page (PLP grid, search grid, each recommendations
     * section). Recommendations events carry their section's gridSelector in
     * the detail; grid modules dispatch without one, which maps to the default
     * grid. Only process events for THIS beacon's grid — otherwise a recs
     * carousel rendering on a PLP would overwrite the category beacon's
     * responseId and fire mis-attributed render/impression events (and vice
     * versa).
     */
    #isOwnGridEvent(e) {
      const eventGrid = e.detail?.gridSelector || '#ss-results-grid';
      return eventGrid === this.#gridSelector;
    }

    #onResultsRendered(response) {
      if (!response) return;

      // Store responseId for linking impression/click events back to this search
      this.#lastResponseId = response.pagination?.responseId
        || response.responseId
        || '';

      this.#sendRender(response);
      this.#observeImpressions(response);
    }

    /**
     * Public entry point for modules that render outside the grid flow
     * (autocomplete dropdown). Sends the render event for this response and
     * observes impressions/product-cards within the given root element.
     * Clicks must be reported separately via trackClickthrough().
     */
    trackResults(response, root) {
      if (!response) return;

      this.#lastResponseId = response.pagination?.responseId
        || response.responseId
        || '';

      this.#sendRender(response);
      this.#observeImpressions(response, root);
    }

    /**
     * Public clickthrough for modules that manage their own click handling
     * (autocomplete dropdown).
     */
    trackClickthrough(productId) {
      if (!productId) return;

      this.#send(this.#module, 'clickthrough', {
        responseId: this.#lastResponseId,
        results: [{
          type: 'product',
          uid: String(productId),
          parentId: String(productId),
        }],
      });
    }

    #sendRender(response) {
      if (!this.#lastResponseId) return;

      // Search/category/autocomplete render is responseId-only — SS joins the
      // rest (query, filters, results) from the API request server-side. This
      // also means a zero-results search still sends its render event, which
      // is what the Zero Results report counts.
      if (this.#module !== 'recommendations') {
        this.#send(this.#module, 'render', {
          responseId: this.#lastResponseId,
        });
        return;
      }

      // Recommendations render carries the rendered product list (plus the
      // profile tag added in #send) to populate the report's Impression Rate.
      const results = (response.results || [])
        .map((p) => {
          const uid = String(p.uid || p.id || '');
          return uid ? { type: 'product', uid, parentId: uid } : null;
        })
        .filter(Boolean);

      if (results.length === 0) return;

      this.#send(this.#module, 'render', {
        responseId: this.#lastResponseId,
        results,
      });
    }

    #onProductClick(e) {
      const card = e.target.closest('product-card');
      if (!card) return;

      const productId = card.getAttribute('data-product-id');
      if (!productId) return;

      this.#send(this.#module, 'clickthrough', {
        responseId: this.#lastResponseId,
        results: [{
          type: 'product',
          uid: productId,
          parentId: productId,
        }],
      });
    }

    #onCardsAppended(detail) {
      if (!detail) return;

      const { response, elements } = detail;

      // Update responseId from the new page's response. Each infinite-scroll
      // page is its own API response, so it needs its own render event to
      // anchor the impressions/clicks that will reference its responseId.
      if (response) {
        const newResponseId = response.pagination?.responseId
          || response.responseId
          || '';
        if (newResponseId && newResponseId !== this.#lastResponseId) {
          this.#lastResponseId = newResponseId;
          this.#sendRender(response);
        }
      }

      // Ensure we have an observer (initial page created one)
      if (!this.#observer) return;

      // Observe only the newly appended card elements
      if (elements) {
        for (const el of elements) {
          const card = el.querySelector('product-card') || (el.tagName === 'PRODUCT-CARD' ? el : null);
          if (card) this.#observer.observe(card);
        }
      }
    }

    // root: optional element to observe product-cards within (autocomplete
    // dropdown). Defaults to the configured grid selector.
    #observeImpressions(response, root) {
      if (this.#observer) {
        this.#observer.disconnect();
      }

      this.#impressedProducts.clear();

      this.#observer = new IntersectionObserver(
        (entries) => {
          const newImpressions = [];

          for (const entry of entries) {
            if (entry.intersectionRatio < IMPRESSION_THRESHOLD) continue;

            const card = entry.target;
            const productId = card.getAttribute('data-product-id');
            if (!productId || this.#impressedProducts.has(productId)) continue;

            this.#impressedProducts.add(productId);
            this.#observer.unobserve(card);

            newImpressions.push({
              type: 'product',
              uid: productId,
              parentId: productId,
            });
          }

          // Batch impressions into a single beacon call
          if (newImpressions.length > 0) {
            this.#send(this.#module, 'impression', {
              responseId: this.#lastResponseId,
              results: newImpressions,
            });
          }
        },
        { threshold: IMPRESSION_THRESHOLD }
      );

      const cards = root
        ? root.querySelectorAll('product-card')
        : document.querySelectorAll(`${this.#gridSelector} product-card`);
      cards.forEach((card) => this.#observer.observe(card));
    }

    #buildContext() {
      return {
        timestamp: new Date().toISOString(),
        pageUrl: window.location.href,
        userId: getOrCreateContextId(USER_ID_KEY, 'local'),
        sessionId: getOrCreateContextId(SESSION_ID_KEY, 'session'),
        pageLoadId: this.#pageLoadId,
        initiator: 'searchspring/custom/1.0',
      };
    }

    /**
     * Report a beacon event that SS did not accept (ECSR-504).
     *
     * Logged rather than thrown — a rejected analytics event must never break
     * the storefront — and mirrored onto a DOM event so monitoring or GTM can
     * pick it up without patching this file.
     */
    #reportFailure(module, event, reason) {
      console.warn(`[SS Beacon] ${module}/${event} not accepted by SS: ${reason}`);
      try {
        document.dispatchEvent(
          new CustomEvent('ss:beacon-error', {
            detail: { module, event, reason: String(reason), siteId: this.#siteId },
          })
        );
      } catch (err) {
        /* CustomEvent unavailable — the console warning above still stands */
      }
    }

    #send(module, event, data) {
      try {
        const url = `${BEACON_BASE}/${this.#siteId}/${module}/${event}`;
        // SS requires `tag` (profile) on every recommendations event; without it
        // the event is dropped/unattributed and CTR never populates.
        const eventData = this.#tag ? { tag: this.#tag, ...data } : data;
        const payload = JSON.stringify({
          context: this.#buildContext(),
          data: eventData,
        });

        // fetch(keepalive) in preference to navigator.sendBeacon.
        //
        // sendBeacon reports only whether the browser *queued* the request; its
        // response is opaque, so an event SS rejects is lost in silence. That
        // blind spot is why ECSR-504 was hard to pin down: SS's own Beacon Data
        // Quality report listed category.render as missing every day while we
        // had no way to see whether our POSTs were being accepted. (They are —
        // the endpoint answers 200 {"success":true} — but we could not prove it
        // from the storefront.)
        //
        // keepalive gives the same survive-the-navigation guarantee sendBeacon
        // provides, which matters for clickthrough events fired as the page
        // unloads, while leaving the response readable. Content type stays
        // text/plain: that is what sendBeacon sent for a string body, SS accepts
        // it, and this change is about visibility, not about altering the
        // request. sendBeacon remains the fallback where fetch is unavailable or
        // rejects keepalive outright.
        let dispatched = false;
        if (typeof fetch === 'function') {
          try {
            fetch(url, {
              method: 'POST',
              body: payload,
              headers: { 'Content-Type': 'text/plain' },
              keepalive: true,
            })
              .then((response) => {
                if (!response.ok) {
                  this.#reportFailure(module, event, `HTTP ${response.status}`);
                }
              })
              .catch((err) => {
                this.#reportFailure(module, event, err);
              });
            dispatched = true;
          } catch (err) {
            // Synchronous throw (e.g. keepalive unsupported) — fall through.
            dispatched = false;
          }
        }

        if (!dispatched) {
          const queued = navigator.sendBeacon?.(url, payload);
          if (queued === false) {
            this.#reportFailure(module, event, 'sendBeacon refused to queue');
          }
        }
      } catch (err) {
        console.error('[SS Beacon]', err);
      }
    }
  }

  window.SSBeacon = SSBeacon;
})();
